import { useEffect, useRef, useState } from "react";

import { http } from "@/utils/http";
import { CqWord } from "@/utils/word";

interface UseDocumentUploadReturn {
  documentVersionId: null | { document_id?: number; version_id?: number };
  isUploading: boolean;
  uploadError: Error | null;
  retryUpload: () => void;
}

/**
 * Custom hook to handle document upload to the server
 * Ensures the document is only uploaded once per session
 */
export const useDocumentUpload = (): UseDocumentUploadReturn => {
  const [documentVersionId, setDocumentVersionId] = useState<{
	document_id?: number;
	version_id?: number;
  }>(
    null
  );
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<Error | null>(null);
  const hasUploadedDocument = useRef(false);

  const retryUpload = () => {
    hasUploadedDocument.current = false;
    setDocumentVersionId(null);
    setUploadError(null);
  };

  useEffect(() => {
    const uploadDocumentFile = async () => {
      // Skip if already uploaded in this session
      if (hasUploadedDocument.current) {
        return;
      }

      setIsUploading(true);
      setUploadError(null);

      try {
        const title = await CqWord.getDocumentTitle();
        const documentFile = await CqWord.getDocumentAsFile(title);

        if (!documentFile) {
          throw new Error("No document file returned from getDocumentAsFile");
        }

        const formData = new FormData();
        formData.append("file", documentFile);

        const response = await http.post<{
          document_id?: number;
          version_id?: number;
        }>(`/api/plugin/documents`, formData);

        if (response.data?.version_id && response.data?.document_id) {
          setDocumentVersionId({
			document_id: response.data.document_id,
			version_id: response.data.version_id,
		  });
          hasUploadedDocument.current = true;
        }
      } catch (error) {
        const err =
          error instanceof Error ? error : new Error("Unknown upload error");
        setUploadError(err);
        console.error("Error in uploadDocumentFile:", err);
      } finally {
        setIsUploading(false);
      }
    };

    uploadDocumentFile();
  }, []);

  return { documentVersionId, isUploading, retryUpload, uploadError };
};
